import Card from "../../../shared/Card";
import { parseFixed, renderAmount } from "../../../utils";

/**
 * PayoutTotals Component - Displays various totals for payouts
 * @param {Object} info - Contains all the total values
 * @param {Function} handleUpdate - Handler for updating total values
 * @param {string} appType - Type of application to filter totals
 */
const PayoutTotals = ({ info, handleUpdate, appType }) => {
  const legs = Array.isArray(info?.legs) ? info.legs : [];

  const getLegMileage = (leg) => {
    if (leg?.useCustomManifestMileage) {
      return Number(leg?.customManifestMileage) || 0;
    }

    return Number(leg?.route?.mileage) || 0;
  };

  const isUnloadedLeg = (leg) => {
    const loadType = String(leg?.loadType || "")
      .trim()
      .toLowerCase();
    const payoutType = String(leg?.payoutType || "")
      .trim()
      .toLowerCase();

    return loadType === "empty" || payoutType === "empty";
  };

  const unloadedMiles = legs.reduce((sum, leg) => {
    const isUnloaded = isUnloadedLeg(leg);
    if (!isUnloaded) return sum;
    return sum + getLegMileage(leg);
  }, 0);

  const loadedMiles = legs.reduce((sum, leg) => {
    const isLoaded = !isUnloadedLeg(leg);
    if (!isLoaded) return sum;
    return sum + getLegMileage(leg);
  }, 0);

  // Define which totals to show for each appType
  const totalConfig = {
    trucking: [
      "total",
      "totalMiles",
      "loadedMiles",
      "unloadedMiles",
      "mileageTotal",
      "supplementalTotal",
      "hours",
      "hourlyTotal",
    ],
    default: ["total", "totalMiles", "mileageTotal"],
    serviceRepair: ["total", "hours", "hourlyTotal", "supplementalTotal"],
  };

  // Get the appropriate totals to display based on appType
  const visibleTotals = totalConfig[appType] || totalConfig.default;

  // Helper function to render total item
  const renderTotalItem = (key) => {
    const totalsMap = {
      total: (
        <div className="flex flex-col" key="total">
          <span className="text-gray-500 mb-1 font-semibold">Total</span>
          <span className="font-semibold text-lg">
            <p
              type="text"
              id="total"
              displayText={(e) => (
                <span className="font-semibold text-lg">{e}</span>
              )}
              value={info.total}
              onSave={handleUpdate}
            >
              {renderAmount(Number(info.total))}
            </p>
          </span>
        </div>
      ),
      loadedMiles: (
        <div className="flex flex-col" key="loadedMiles">
          <span className="text-gray-500 mb-1 font-semibold">Loaded Miles</span>
          <span className="font-semibold text-lg">
            <p>{parseFixed(loadedMiles || 0).toLocaleString("en-US")} mi</p>
          </span>
        </div>
      ),
      unloadedMiles: (
        <div className="flex flex-col" key="unloadedMiles">
          <span className="text-gray-500 mb-1 font-semibold">
            Unloaded Miles
          </span>
          <span className="font-semibold text-lg">
            <p>{parseFixed(unloadedMiles || 0).toLocaleString("en-US")} mi</p>
          </span>
        </div>
      ),
      totalMiles: (
        <div className="flex flex-col" key="totalMiles">
          <span className="text-gray-500 mb-1 font-semibold">Total Miles</span>
          <span className="font-semibold text-lg">
            <p>
              {parseFixed(info.totalMileage || 0).toLocaleString("en-US")} mi
            </p>
          </span>
        </div>
      ),
      mileageTotal: (
        <div className="flex flex-col" key="mileageTotal">
          <span className="text-gray-500 mb-1 font-semibold">
            Mileage Total
          </span>
          <span className="font-semibold text-lg">
            <p
              type="number"
              id="mileageTotal"
              displayText={(e) => (
                <span className="font-semibold text-lg">{e}</span>
              )}
              value={info.mileageTotal}
              onSave={handleUpdate}
            >
              {renderAmount(info.mileageTotal)}
            </p>
          </span>
        </div>
      ),
      supplementalTotal: (
        <div className="flex flex-col" key="supplementalTotal">
          <span className="text-gray-500 mb-1 font-semibold">
            Supplemental Total
          </span>
          <span className="font-semibold text-lg">
            <p
              type="number"
              id="supplementalTotal"
              displayText={(e) => (
                <span className="font-semibold text-lg">{e}</span>
              )}
              value={info.supplementalTotal}
              onSave={handleUpdate}
            >
              {renderAmount(info.supplementalTotal)}
            </p>
          </span>
        </div>
      ),
      hours: (
        <div className="flex flex-col" key="hours">
          <span className="text-gray-500 mb-1 font-semibold">Total Hours</span>
          <span className="font-semibold text-lg">
            <p
              type="number"
              id="hours"
              displayText={(e) => (
                <span className="font-semibold text-lg">{e}</span>
              )}
              value={info.hours}
              onSave={handleUpdate}
            >
              {parseFixed(info.hours)}
            </p>
          </span>
        </div>
      ),
      hourlyTotal: (
        <div className="flex flex-col" key="hourlyTotal">
          <span className="text-gray-500 mb-1 font-semibold">Hourly Total</span>
          <span className="font-semibold text-lg">
            <p
              type="number"
              id="hourlyTotal"
              displayText={(e) => (
                <span className="font-semibold text-lg">{e}</span>
              )}
              value={info.hourlyTotal}
              onSave={handleUpdate}
            >
              {renderAmount(info.hourlyTotal)}
            </p>
          </span>
        </div>
      ),
    };

    return totalsMap[key];
  };

  return (
    <Card className="row-span-2 px-5 py-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 lg:grid-cols-4 xl:grid-cols-8">
        {visibleTotals.map((totalKey) => renderTotalItem(totalKey))}
      </div>
    </Card>
  );
};

export default PayoutTotals;
